import {Injectable, signal} from '@angular/core';

/**
 * Whether the **floating** {@link ContextSwitcherComponent} pill is currently on screen — the single source of
 * truth for any OTHER surface that needs to know, so it never has to re-derive "does this account have more
 * than one place to work" itself.
 *
 * ## Why this exists (#575)
 *
 * The pill is `position: fixed`, bottom-left, on every shell (see the component's own remarks) — so it takes
 * part in no host's layout flow. A sidebar sitting under its corner has no way to reserve space for it short of
 * answering the exact question the pill itself already answers from `/auth/context/available` — and asking a
 * second time, from a second place, would mean two independent fetches that can briefly disagree (a reserved
 * gap appearing or disappearing a beat before or after the pill itself does), on top of being the same kind of
 * duplicated business rule this codebase keeps in ONE place elsewhere (the central booking calculator, the
 * central role vocabulary, `ErrorCatalog`, …).
 *
 * So `ContextSwitcherComponent` is the only writer — it mirrors its own rendering decision into this service
 * via an `effect()` — and anything else (today: the employee panel's sidebar) only ever reads. A reader gets
 * `false` until the component's first fetch resolves, which is the safe default: no reserved gap is a cosmetic
 * one-frame gap at worst, never a hidden control.
 *
 * ## What it means since #602
 *
 * The question is "is something floating over your bottom-left corner", **not** "does this account have more
 * than one context". Those used to be the same thing and are not any more: below Tailwind's `sm` the switcher
 * renders as a chip inside the shell's own top bar instead, and nothing floats over anything. So on a phone
 * this signal reads `false` even for an account with a dozen contexts, and the sidebar's reserved gap correctly
 * disappears — which is the whole point, because on that width the gap was reserving room for an element that
 * is no longer there.
 *
 * Only the floating declaration writes. A header declaration deliberately writes nothing at all rather than
 * writing `false`: with several declarations mounted, two writers would race for one slot and the answer would
 * depend on effect ordering.
 */
@Injectable({providedIn: 'root'})
export class ContextSwitcherPresenceService {
  private readonly _visible = signal(false);

  /** Whether the floating pill is currently rendered. */
  readonly visible = this._visible.asReadonly();

  /** Called only by {@link ContextSwitcherComponent}, and only by its `floating` declaration. */
  setVisible(value: boolean): void {
    this._visible.set(value);
  }
}
